import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { RequestLoadData } from 'shared/api/lib'
import { UsersApi } from 'entities/Users/api/users'
import {
  type IParamsUserAccessRequestsList,
  type IResponseUserAccessRequest,
} from 'entities/Users/api/types'
import { type UserAccessRequestType } from 'entities/Users'
import { AccessRequest } from 'entities/Users/model/AccessRequest'
import { RowAccessRequest } from 'entities/Users/model/Table/RowAccessRequest'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class AccessRequestsStore {
  private readonly _tableStore: TableStore<RowAccessRequest>
  private _itemsMap: Map<number, AccessRequest> = new Map()
  private _search = ''
  private _scrollToTopTrigger = ''

  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null

  constructor(
    readonly requestType: UserAccessRequestType,
    tableKey: string,
    readonly entities: IEntitiesStore
  ) {
    this._tableStore = new TableStore({
      key: tableKey,
      element: 'accessRequests',
      limit: 10,
      page: 1,
    })
    makeAutoObservable(this)
  }

  init = () => {
    this.loadData()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  private _setItem = (data: IResponseUserAccessRequest) => {
    runInAction(() => {
      this._itemsMap.set(data.id, new AccessRequest(data))
    })
  }

  setSearch = (value: string) => {
    this._search = value
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this._tableStore.setLoading(true)
      })

      this._tableStore.setBulkTotal(0)
      this._tableStore.bulkStore.resetBulkAll()

      const { data } = await UsersApi.getUsersAccessRequestsList(this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._itemsMap.clear()
        data.data.forEach((item) => {
          this._setItem(item)
        })
        this._tableStore.setTotal(data.meta.total)
        this._scrollToTopTrigger = nanoid()
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this._tableStore.setLoading(false)
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get paramsWithoutPage(): Omit<IParamsUserAccessRequestsList, 'page'> {
    return {
      per_page: this._tableStore.limit,
      search: this._search || undefined,
      type: this.requestType,
      sortBy: this._tableStore.sortBy || undefined,
      sortOrder: this._tableStore.sortOrder || undefined,
    }
  }

  get paramsGetItems(): IParamsUserAccessRequestsList {
    return {
      page: this._tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get itemsRows() {
    return this.items.map((item) => new RowAccessRequest(item, this.entities))
  }

  get tableStore() {
    return this._tableStore
  }

  get loading() {
    return this._tableStore.loading
  }

  get search() {
    return this._search
  }

  get scrollToTopTrigger() {
    return this._scrollToTopTrigger
  }

  get total() {
    return this._tableStore.total
  }

  get isReviewDisabledByInboxAccess() {
    const canManageInboxes = this.entities.userPermissionsStore.getItem(
      'can_manage_organization_inboxes'
    )
    const isOwnerInAnyInbox = this.entities.inboxesStore.hasInboxWithCurrentUserAsOwner

    return !canManageInboxes && !isOwnerInAnyInbox
  }

  get isGracePeriod() {
    return this.entities.organizationStore.isNumbersGracePeriod
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
    this._itemsMap.clear()
    this._search = ''
  }
}
